"use client"

import { useState } from "react"
import { useTranslations } from "next-intl"
import { toast } from "sonner"
import type { BooruPost } from "@/lib/booru-api"
import { detectPlatform, supportsNativeDownloads } from "@/lib/platform-utils"
import { downloadNative } from "@/lib/native-download"
import { downloadExtension } from "@/lib/download-extension"
import { findMediaHost } from "@/lib/media-hosts"
import { mediaWorkerDownloadUrl } from "@/lib/image-utils"

// Moved out of components/booru-detail-modal.tsx (same state and functions).

export function useModalDownloads({
  post,
  isAlbum,
  currentAlbumImageUrl,
  currentAlbumPage,
  effectiveAlbumImages,
  autoDownloadWithoutDialog,
}: {
  post: BooruPost | null
  isAlbum: boolean
  currentAlbumImageUrl: string | null | undefined
  currentAlbumPage: number
  effectiveAlbumImages: string[]
  autoDownloadWithoutDialog: boolean
}) {
  const tt = useTranslations("booruDetailModal")
  const [isZipDownloading, setIsZipDownloading] = useState(false)
  const [zipDownloadProgress, setZipDownloadProgress] = useState(0)

  const handleDownload = async () => {
    const downloadUrl = isAlbum && currentAlbumImageUrl ? currentAlbumImageUrl : post?.fileUrl
    if (!downloadUrl) return

    const platform = detectPlatform()
    console.log(`[download] Platform: ${platform}, URL:`, downloadUrl)

    try {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
      const pageNum = isAlbum ? `_page${currentAlbumPage + 1}` : ""
      const extension = downloadExtension(downloadUrl, post?.type, (post?.rawPost as { mime?: string } | undefined)?.mime)
      const filename = `booru_${post?.id}${pageNum}.${extension}`

      // iOS share functionality (works on both web and native iOS)
      if (isIOS && navigator.share) {
        try {
          const response = await fetch(downloadUrl)
          const blob = await response.blob()
          const file = new File([blob], filename, { type: blob.type })

          await navigator.share({
            files: [file],
            title: `Booru Post ${post?.id}`,
          })
          if (!autoDownloadWithoutDialog) {
            toast.success(tt("imageSharedSuccessfully"))
          }
          return
        } catch (shareError) {
          console.log("[download] iOS share failed, trying alternative methods:", shareError)
        }
      }

      // NATIVE APP DOWNLOAD (Capacitor/React Native) - No CORS restrictions!
      if (supportsNativeDownloads()) {
        console.log("[download] Using native download (no proxy needed)")
        
        const success = await downloadNative(downloadUrl, filename)
        
        if (success) {
          if (!autoDownloadWithoutDialog) {
            toast.success(tt("downloadComplete"))
          }
          return
        } else {
          console.log("[download] Native download failed, falling back to web methods")
        }
      }

      // WEB/PWA DOWNLOAD - Try direct first, then proxy fallback
      console.log("[download] Using web download methods")

      // Try the visitor's own browser first: it costs the server nothing. It only works when the file host lets other sites read its
      // files (CORS), so it is a real CORS read (the old "no-cors" fetch always returned an empty body and never downloaded anything),
      // sent without our address as referrer so hotlink checks don't refuse it. There is no size cap: a big video is the visitor's own
      // download and costs us nothing. Anything that fails (including a file the browser cannot hold) goes to the streaming proxy below. (Checked 2026-10-26: Rule34, Gelbooru, Danbooru, Safebooru, Yande.re, Xbooru and TBIB send no
      // CORS header and e621/e6AI only allow their own pages, so for those the proxy is what runs.)
      try {
        console.log("[download] Attempting direct download")
        const response = await fetch(downloadUrl, { mode: "cors", credentials: "omit", referrerPolicy: "no-referrer" })
        if (!response.ok) throw new Error(`HTTP ${response.status}`)
        const blob = await response.blob()
        
        // Check if we got actual content (blob size > 0)
        if (blob.size > 0) {
          const blobUrl = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = blobUrl
          a.download = filename
          a.style.display = "none"
          document.body.appendChild(a)
          a.click()

          if (!autoDownloadWithoutDialog) {
            toast.success(tt("downloadStarted"))
          }

          setTimeout(() => {
            document.body.removeChild(a)
            URL.revokeObjectURL(blobUrl)
          }, 500)
          return
        }
      } catch (directError) {
        console.log("[download] Direct download failed, trying proxy:", directError)
      }

      // A host that serves a link the browser can save straight from the host (lib/media-hosts.ts directDownload) is saved by the visitor's own
      // browser: a plain navigation, no bytes through our server.
      const saveDirectly = findMediaHost(downloadUrl)?.directDownload?.(downloadUrl)
      if (saveDirectly) {
        const link = document.createElement("a")
        link.href = saveDirectly
        link.rel = "noopener noreferrer"
        link.style.display = "none"
        document.body.appendChild(link)
        link.click()
        if (!autoDownloadWithoutDialog) toast.success(tt("downloadStarted"))
        setTimeout(() => document.body.removeChild(link), 500)
        return
      }

      // The media Worker (Cloudflare does not bill for the data it sends) streams the file with a Content-Disposition, so the browser saves it
      // straight to disk, however big. A HEAD first (the Worker allows our pages to read it) so a refusal or a limit falls through to the next
      // way instead of becoming a failed download.
      const viaWorker = mediaWorkerDownloadUrl(downloadUrl, filename)
      if (viaWorker) {
        try {
          const probe = await fetch(viaWorker, { method: "HEAD", credentials: "omit" })
          if (probe.ok) {
            const link = document.createElement("a")
            link.href = viaWorker
            link.rel = "noopener" // not noreferrer: the Worker knows our pages by the Referer a plain link sends
            link.style.display = "none"
            document.body.appendChild(link)
            link.click()
            if (!autoDownloadWithoutDialog) toast.success(tt("downloadStarted"))
            setTimeout(() => document.body.removeChild(link), 500)
            return
          }
        } catch (workerError) {
          console.log("[download] Media Worker download failed, trying the next way:", workerError)
        }
      }

      // A host whose files are too big for our server (lib/media-hosts.ts downloadWorkerOnly) never uses the streaming proxy: the catch below
      // opens the file on the host in a new tab instead.
      if (findMediaHost(downloadUrl)?.downloadWorkerOnly) throw new Error("Worker-only host: the media Worker could not save it")

      // Fallback to streaming proxy for large files or CORS-blocked content
      console.log("[download] Using streaming proxy")
      const proxyUrl = `/api/proxy/download?url=${encodeURIComponent(downloadUrl)}&filename=${encodeURIComponent(filename)}`
      
      const a = document.createElement("a")
      a.href = proxyUrl
      a.download = filename
      a.style.display = "none"
      document.body.appendChild(a)
      a.click()

      if (!autoDownloadWithoutDialog) {
        toast.success(tt("downloadStarted"))
      }

      setTimeout(() => {
        document.body.removeChild(a)
      }, 500)

    } catch (error) {
      console.error("[download] All download methods failed:", error)
      toast.error(tt("failedToDownloadOpeningInNewTabInstead"))

      const downloadUrl = isAlbum && currentAlbumImageUrl ? currentAlbumImageUrl : post?.fileUrl
      if (downloadUrl) {
        window.open(downloadUrl, "_blank")
      }
    }
  }

  const handleDownloadAllAsZip = async () => {
    if (!post || !effectiveAlbumImages || effectiveAlbumImages.length === 0) return
    if (isZipDownloading) return

    setIsZipDownloading(true)
    setZipDownloadProgress(0)

    try {
      const { default: JSZip } = await import("jszip")
      const zip = new JSZip()
      const total = effectiveAlbumImages.length
      let succeeded = 0

      for (let i = 0; i < total; i++) {
        const url = effectiveAlbumImages[i]
        try {
          // Route through the streaming proxy — most album sources (erome, doujins,
          // hitomi, xlecx) don't set CORS headers, so a direct browser fetch fails silently.
          const proxyUrl = `/api/proxy/download?url=${encodeURIComponent(url)}&zip=1`
          const response = await fetch(proxyUrl)
          if (!response.ok) throw new Error(`HTTP ${response.status}`)
          const blob = await response.blob()
          const extension = url.split(".").pop()?.split("?")[0]?.slice(0, 5) || "jpg"
          const fileName = `page${String(i + 1).padStart(3, "0")}.${extension}`
          zip.file(fileName, blob)
          succeeded++
        } catch (err) {
          console.error(`[download] Failed to fetch album page ${i + 1}:`, err)
        }
        setZipDownloadProgress(Math.round(((i + 1) / total) * 100))
      }

      if (succeeded === 0) {
        toast.error(tt("couldnTDownloadAnyPagesTheSourceMayBe"))
        return
      }

      const zipBlob = await zip.generateAsync({ type: "blob" })
      const blobUrl = URL.createObjectURL(zipBlob)
      const a = document.createElement("a")
      a.href = blobUrl
      a.download = `${post.site}_${post.id}_album.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(blobUrl)

      toast.success(
        succeeded === total
          ? tt("downloadedAllTotalPagesAsZIP", { total: total })
          : tt("downloadedSucceededTotalPagesAsZIPV", { succeeded: succeeded, total: total, value: total - succeeded }),
      )
    } catch (error) {
      console.error("[download] Album zip download failed:", error)
      toast.error(tt("failedToCreateZIPArchive"))
    } finally {
      setIsZipDownloading(false)
      setZipDownloadProgress(0)
    }
  }

  return { handleDownload, handleDownloadAllAsZip, isZipDownloading, zipDownloadProgress }
}
